import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Filter, SortAsc, SortDesc, Search, Table, CheckCircle, RotateCcw } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { ModuleSuccess } from '../../ui/ModuleSuccess';
import { LessonWrapper, LessonPage } from '../../ui/LessonWrapper';

// ============ LESSON CONTENT ============
const LESSONS: LessonPage[] = [
    {
        id: 1,
        title: "The Golden Sieve",
        icon: "⚜️",
        content: (
            <div className="space-y-6">
                <div className="bg-amber-50 p-6 rounded-2xl border-2 border-amber-200">
                    <h3 className="font-bold text-xl text-amber-800 mb-3">Filtering Data</h3>
                    <p className="text-slate-700 mb-4">
                        Imagine looking for gold in a river. You use a sieve to keep the gold and let the dirt wash away.
                    </p>
                    <p className="text-slate-700">
                        <strong>Filters</strong> (the `WHERE` clause in SQL) allow you to keep only the rows you care about.
                    </p>
                </div>
            </div>
        )
    },
    {
        id: 2,
        title: "Line 'Em Up",
        icon: "📏",
        content: (
            <div className="space-y-6">
                <p className="text-slate-600 mb-4">Once you have your data, you need to organize it.</p>
                <div className="grid grid-cols-2 gap-4">
                    <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                        <div className="font-bold text-blue-700 mb-2">ASC (Ascending)</div>
                        <ul className="text-xs text-blue-600 space-y-1">
                            <li>• A to Z</li>
                            <li>• 0 to 9</li>
                            <li>• Old to New</li>
                        </ul>
                    </div>
                    <div className="bg-amber-50 p-4 rounded-xl border border-amber-200">
                        <div className="font-bold text-amber-700 mb-2">DESC (Descending)</div>
                        <ul className="text-xs text-amber-600 space-y-1">
                            <li>• Z to A</li>
                            <li>• 9 to 0</li>
                            <li>• New to Old</li>
                        </ul>
                    </div>
                </div>
            </div>
        )
    },
    {
        id: 3,
        title: "The Mission",
        icon: "🎯",
        content: (
            <div className="space-y-6">
                <div className="bg-slate-100 p-6 rounded-2xl border-2 border-slate-300">
                    <h3 className="font-bold text-xl text-slate-800 mb-3">Data Sifter 3000</h3>
                    <p className="text-slate-700 mb-4">
                        We have a messy list of primates.
                    </p>
                    <p className="font-bold text-slate-900">
                        Complete 3 missions to pass: Filter by Species, Find a Name, and Sort by Age.
                    </p>
                </div>
            </div>
        )
    }
];

// ============ GAME LOGIC ============
interface DataRow {
    id: number;
    name: string;
    species: string;
    age: number;
    habitat: string;
}

const INITIAL_DATA: DataRow[] = [
    { id: 1, name: 'George', species: 'Gorilla', age: 12, habitat: 'Mountain' },
    { id: 2, name: 'Luna', species: 'Gorilla', age: 8, habitat: 'Rainforest' },
    { id: 3, name: 'Max', species: 'Chimpanzee', age: 5, habitat: 'Rainforest' },
    { id: 4, name: 'Bella', species: 'Orangutan', age: 15, habitat: 'Swamp' },
    { id: 5, name: 'Oscar', species: 'Gorilla', age: 22, habitat: 'Mountain' },
    { id: 6, name: 'Mia', species: 'Chimpanzee', age: 3, habitat: 'Savanna' },
    { id: 7, name: 'Leo', species: 'Orangutan', age: 9, habitat: 'Rainforest' },
    { id: 8, name: 'Zara', species: 'Gorilla', age: 6, habitat: 'Rainforest' },
];

const MISSIONS = [
    {
        id: 1,
        text: "Mission 1: Find all the Orangutans.",
        hint: "Use the Species Filter.",
        check: (data: DataRow[], params: any) => params.species === 'Orangutan'
    },
    {
        id: 2,
        text: "Mission 2: Find 'Max'.",
        hint: "Use the Search bar.",
        check: (data: DataRow[], params: any) => data.length === 1 && data[0].name === 'Max'
    },
    {
        id: 3,
        text: "Mission 3: Organize all Gorillas from Youngest to Oldest.",
        hint: "Filter by Gorilla AND Sort by Age (Ascending).",
        check: (data: DataRow[], params: any) => params.species === 'Gorilla' && params.sort === 'age' && params.dir === 'asc'
    }
];

const FilterGame = ({ onComplete }: { onComplete: () => void }) => {
    const [data, setData] = useState(INITIAL_DATA);
    const [searchTerm, setSearchTerm] = useState('');
    const [speciesFilter, setSpeciesFilter] = useState('all');
    const [sortField, setSortField] = useState<keyof DataRow | null>(null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [completed, setCompleted] = useState(false);

    // Mission State
    const [missionIndex, setMissionIndex] = useState(0);
    const [missionComplete, setMissionComplete] = useState(false);
    const currentMission = MISSIONS[missionIndex];

    // Apply filters and sorting
    useEffect(() => {
        let result = [...INITIAL_DATA];

        // Filter by search
        if (searchTerm) {
            result = result.filter(row =>
                row.name.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Filter by species
        if (speciesFilter !== 'all') {
            result = result.filter(row => row.species === speciesFilter);
        }

        // Sort
        if (sortField) {
            result.sort((a, b) => {
                const aVal = a[sortField];
                const bVal = b[sortField];
                if (typeof aVal === 'number' && typeof bVal === 'number') {
                    return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
                }
                return sortDirection === 'asc'
                    ? String(aVal).localeCompare(String(bVal))
                    : String(bVal).localeCompare(String(aVal));
            });
        }

        setData(result);
    }, [searchTerm, speciesFilter, sortField, sortDirection]);

    // Check Mission Completion
    useEffect(() => {
        if (completed) return;

        const params = {
            searchTerm,
            species: speciesFilter,
            sort: sortField,
            dir: sortDirection
        };

        if (currentMission && currentMission.check(data, params)) {
            setMissionComplete(true);
            setTimeout(() => {
                if (missionIndex < MISSIONS.length - 1) {
                    setMissionIndex(prev => prev + 1);
                    setMissionComplete(false);
                } else {
                    setCompleted(true);
                    onComplete();
                }
            }, 1500);
        }
    }, [data, searchTerm, speciesFilter, sortField, sortDirection, missionIndex, completed]);

    const handleSort = (field: keyof DataRow) => {
        if (sortField === field) {
            setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const reset = () => {
        setSearchTerm('');
        setSpeciesFilter('all');
        setSortField(null);
        setData(INITIAL_DATA);
    };

    const species = [...new Set(INITIAL_DATA.map(d => d.species))];

    if (completed) {
        return (
            <div className="bg-slate-100 rounded-2xl p-6 text-left border-2 border-slate-200 text-center">
                <div className="text-6xl mb-4">🏆</div>
                <h4 className="font-bold text-slate-800 mb-2">Mission Complete!</h4>
                <p className="text-slate-600 mb-4 text-sm">
                    You've proved you can slice and dice data like a pro.
                </p>
                <button
                    onClick={() => {
                        setCompleted(false);
                        setMissionIndex(0);
                        reset();
                    }}
                    className="bg-amber-500 text-white px-6 py-2 rounded-xl font-bold hover:bg-amber-600"
                >
                    Play Again
                </button>
            </div>
        )
    }

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Mission Banner */}
            <div className="lg:col-span-3 bg-white p-4 rounded-xl border-2 border-blue-100 shadow-sm flex items-center justify-between">
                <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
                        MISSION {missionIndex + 1}/{MISSIONS.length}
                    </div>
                    <div className={`font-bold ${missionComplete ? 'text-green-600' : 'text-slate-800'}`}>
                        {missionComplete ? "SUCCESS!" : currentMission.text}
                    </div>
                </div>
                {!missionComplete && (
                    <div className="text-xs bg-blue-50 text-blue-600 px-3 py-1 rounded-full font-bold">
                        Hint: {currentMission.hint}
                    </div>
                )}
            </div>

            {/* Controls */}
            <div className="bg-white rounded-3xl p-6 shadow-xl border-2 border-slate-100">
                <h3 className="font-bold text-slate-700 mb-4 flex items-center gap-2">
                    <Filter className="text-amber-500" /> Query Builder
                </h3>

                {/* Search */}
                <div className="mb-4">
                    <label className="text-sm font-bold text-slate-500 mb-1 block">Search Name</label>
                    <div className="relative">
                        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            placeholder="Type a name..."
                            className="w-full pl-11 pr-4 py-3 rounded-xl border-2 border-slate-200 focus:border-amber-500 outline-none"
                        />
                    </div>
                </div>

                {/* Species Filter */}
                <div className="mb-4">
                    <label className="text-sm font-bold text-slate-500 mb-1 block">Filter by Species</label>
                    <select
                        value={speciesFilter}
                        onChange={e => setSpeciesFilter(e.target.value)}
                        className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-amber-500 outline-none font-bold"
                    >
                        <option value="all">All Species</option>
                        {species.map(s => (
                            <option key={s} value={s}>{s}</option>
                        ))}
                    </select>
                </div>

                <button onClick={reset} className="w-full bg-slate-100 text-slate-600 py-3 rounded-xl font-bold hover:bg-slate-200 flex items-center justify-center gap-2">
                    <RotateCcw size={18} /> Reset Filters
                </button>

                {/* SQL Preview */}
                <div className="mt-6 bg-slate-900 rounded-xl p-4 font-mono text-xs text-green-400">
                    <div className="text-slate-500 mb-2">Generated SQL:</div>
                    SELECT * FROM primates<br />
                    {speciesFilter !== 'all' && <span className="text-amber-400">WHERE species = '{speciesFilter}'<br /></span>}
                    {searchTerm && <span className="text-amber-400">{speciesFilter !== 'all' ? 'AND' : 'WHERE'} name LIKE '%{searchTerm}%'<br /></span>}
                    {sortField && <span className="text-blue-400">ORDER BY {sortField} {sortDirection.toUpperCase()}</span>}
                </div>
            </div>

            {/* Data Table */}
            <div className="lg:col-span-2 bg-white rounded-3xl p-6 shadow-xl border-2 border-slate-100 overflow-hidden">
                <h3 className="font-bold text-slate-700 mb-4 flex items-center gap-2">
                    <Table className="text-blue-500" /> Primate Database
                    <span className="text-sm text-slate-400 ml-auto">{data.length} records</span>
                </h3>

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-slate-50">
                                {['id', 'name', 'species', 'age', 'habitat'].map(field => (
                                    <th
                                        key={field}
                                        onClick={() => handleSort(field as keyof DataRow)}
                                        className="px-4 py-3 text-left font-bold text-slate-600 text-sm cursor-pointer hover:bg-slate-100 transition-colors"
                                    >
                                        <div className="flex items-center gap-1">
                                            {field.toUpperCase()}
                                            {sortField === field && (
                                                sortDirection === 'asc' ? <SortAsc size={14} /> : <SortDesc size={14} />
                                            )}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            <AnimatePresence>
                                {data.map(row => (
                                    <motion.tr
                                        key={row.id}
                                        initial={{ opacity: 0 }}
                                        animate={{ opacity: 1 }}
                                        exit={{ opacity: 0 }}
                                        className="border-t border-slate-100 hover:bg-amber-50"
                                    >
                                        <td className="px-4 py-3 text-slate-400">{row.id}</td>
                                        <td className="px-4 py-3 font-bold text-slate-800">{row.name}</td>
                                        <td className="px-4 py-3">
                                            <span className={`px-2 py-1 rounded-full text-xs font-bold ${row.species === 'Gorilla' ? 'bg-green-100 text-green-700' :
                                                row.species === 'Chimpanzee' ? 'bg-blue-100 text-blue-700' :
                                                    'bg-orange-100 text-orange-700'
                                                }`}>{row.species}</span>
                                        </td>
                                        <td className="px-4 py-3 text-slate-600">{row.age} yrs</td>
                                        <td className="px-4 py-3 text-slate-600">{row.habitat}</td>
                                    </motion.tr>
                                ))}
                            </AnimatePresence>
                        </tbody>
                    </table>

                    {data.length === 0 && (
                        <div className="text-center py-12 text-slate-400">
                            No records match your filters
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ============ MAIN COMPONENT ============
export const FilterSort = () => {
    const { completeModule } = useAcademyStore();
    const [completed, setCompleted] = useState(false);

    const handleComplete = () => {
        setCompleted(true);
        completeModule('filter-sort', 100);
    };

    return (
        <LessonWrapper
            moduleName="Filter & Sort"
            moduleEmoji="🌪️"
            moduleColor="amber"
            lessons={LESSONS}
            completed={completed}
            activityLabel="Data Sifter"
        >
            <FilterGame onComplete={handleComplete} />
        </LessonWrapper>
    );
};

export default FilterSort;
